import React from "react";
import { Button, Spinner } from "react-bootstrap";
import {
  FormControl as MUIFormControl,
  InputLabel,
  Select as MUISelect,
  MenuItem,
} from "@mui/material";
import { SelectChangeEvent } from "@mui/material/Select";
import { apiGet } from "../utils/apiAuth";
import { FaChevronUp, FaChevronDown } from "react-icons/fa";

interface Track {
  track_id: string;
  track_name: string;
}

interface Project {
  project_id: string;
  project_name: string;
  project_allowed_subjects?: string[];
  project_data?: any;
}

interface Subject {
  subject_id: string;
  subject_name: string;
  is_project: boolean;
}

interface Props {
  selectedTrack: string;
  selectedProject: string;
  selectedProjectName: string;
  loading: boolean;
  isFilterApplied?: boolean;
  onTrackChange: (trackId: string) => void;
  onProjectChange: (
    projectId: string,
    projectName: string,
    projectAllowedSubjects?: string[],
    projectData?: any
  ) => void;
  onSubjectsChange: (subjects: Subject[]) => void;
}

const ProjectConfigurationHeader: React.FC<Props> = ({
  selectedTrack,
  selectedProject,
  loading,
  isFilterApplied = false,
  onTrackChange,
  onProjectChange,
  onSubjectsChange,
}) => {
  const [tracks, setTracks] = React.useState<Track[]>([]);
  const [projects, setProjects] = React.useState<Project[]>([]);
  const [loadingTracks, setLoadingTracks] = React.useState(false);
  const [loadingProjects, setLoadingProjects] = React.useState(false);

  const [openStats, setOpenStats] = React.useState(false);

  // ---------------- STATS JSON ----------------
  const [stats, setStats] = React.useState<{
    total: { video: number; notes: number; mcq: number; coding: number; project_coding: number };
    selected: { video: number; notes: number; mcq: number; coding: number; project_coding: number };
    assigned: { video: number; notes: number; mcq: number; coding: number; project_coding: number };
    uploaded: { video: number; notes: number; mcq: number; coding: number; project_coding: number };
    approved: { video: number; notes: number; mcq: number; coding: number; project_coding: number };
  } | null>(null);

  /**
   * Calculates the sum of all numeric values in an object
   * @param obj - Object with numeric values
   * @returns Sum of all values
   */
  const getSum = (obj: Record<string, number>) =>
    Object.values(obj).reduce((a, b) => a + b, 0);

  const gridStyle: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: "110px repeat(4, 90px) 110px",
    alignItems: "center",
  };

  // ---------------- EFFECTS ----------------
  /**
   * Effect: Fetches all tracks on component mount
   * Triggers: Once on mount (empty dependency array)
   */
  React.useEffect(() => {
    fetchTracks();
  }, []);

  /**
   * Effect: Handles track selection change
   * Step 1: If no track selected, clear projects, subjects, and stats
   * Step 2: If track selected, fetch projects and subjects for the track, clear stats
   * Triggers: When selectedTrack changes
   */
  React.useEffect(() => {
    if (!selectedTrack) {
      // Step 1: Clear related data
      setProjects([]);
      onSubjectsChange([]);
      setStats(null);
      return;
    }
    // Step 2: Fetch data for selected track
    fetchProjects(selectedTrack);
    fetchSubjectsForTrack(selectedTrack);
    setStats(null);
  }, [selectedTrack]);

  /**
   * Effect: Fetches content status when project is selected
   * Step 1: If no project selected, clear stats
   * Step 2: If project selected, fetch content status
   * Triggers: When selectedProject changes
   */
  React.useEffect(() => {
    if (!selectedProject) {
      setStats(null);
      return;
    }
    fetchContentStatus(selectedProject);
  }, [selectedProject]);

  /**
   * Effect: Triggers project change callback when projects list updates
   * Step 1: Check if projects list and selectedProject exist
   * Step 2: Find the selected project in the projects list
   * Step 3: Call onProjectChange with project details
   * Triggers: When projects array changes
   */
  React.useEffect(() => {
    // Step 1: Validation
    if (!projects.length) return;
    if (!selectedProject) {
      // Clear stats when no project is selected
      setStats(null);
      return;
    }
  
    // Step 2: Find project
    const project = projects.find(p => p.project_id === selectedProject);
    if (!project) {
      setStats(null);
      return;
    }
  
    // Step 3: Trigger callback
    onProjectChange(
      project.project_id,
      project.project_name,
      project.project_allowed_subjects,
      project.project_data
    );
  }, [projects, selectedProject]);
  
  // ---------------- API CALLS ----------------
  /**
   * Fetches all available tracks for courses
   * Step 1: Set loading state to true
   * Step 2: Fetch tracks data
   * Step 3: Update tracks state
   * Step 4: Reset loading state
   */
  const fetchTracks = async () => {
    setLoadingTracks(true);
    try {
      // Check if tracks are already in session storage
      if(sessionStorage.getItem('tracks') && sessionStorage.getItem('tracks') !== '[]' && sessionStorage.getItem('tracks') !== null) {
        const cachedTracks = JSON.parse(sessionStorage.getItem('tracks') || '[]');
        setTracks(cachedTracks);
      } else {
        // If not, fetch from API
        const response = await apiGet('/api/admin/get_all_tracks_for_courses/');
        if (response.status === 200 || response.status === 201) {
          const tracksData = response.data.tracks || [];
          // Store tracks in session storage
          sessionStorage.setItem('tracks', JSON.stringify(tracksData));
          setTracks(tracksData);
        }
      }
    } catch (error) {
      setTracks([]);
    } finally {
      setLoadingTracks(false);
    }
  };

  /**
   * Fetches all projects for a specific track
   * Step 1: Set loading state to true
   * Step 2: Fetch projects from API
   * Step 3: Update projects state
   * Step 4: Reset loading state
   * @param trackId - The track ID to fetch projects for
   */
  const fetchProjects = async (trackId: string) => {
    setLoadingProjects(true);
    try {
      const res = await apiGet(`/api/admin/get-all-project/${trackId}`);
      setProjects(res.data.projects || []);
    } finally {
      setLoadingProjects(false);
    }
  };

  /**
   * Fetches subjects available for a specific track
   * Step 1: Fetch subjects from API
   * Step 2: Update subjects via callback
   * Step 3: On error, set empty array
   * @param trackId - The track ID to fetch subjects for
   */
  const fetchSubjectsForTrack = async (trackId: string) => {
    try {
      const res = await apiGet(
        `/api/admin/get_subjects_for_track/${trackId}/`
      );
      onSubjectsChange(res.data.subjects || []);
    } catch {
      onSubjectsChange([]);
    }
  };

  /**
   * Fetches content status statistics for a project
   * Step 1: Fetch content status from API
   * Step 2: Check if response contains status data
   * Step 3: Set stats with default values (0) for missing fields
   * Step 4: On error or no data, clear stats
   * @param projectId - The project ID to fetch status for
   */
  const fetchContentStatus = async (projectId: string) => {
    try {
      const res = await apiGet(`/api/admin/content/project/content_status/${projectId}/`);
      if (res.data && (res.data.total || res.data.selected || res.data.assigned || res.data.uploaded || res.data.approved)) {
        setStats({
          total: res.data.total || { video: 0, notes: 0, mcq: 0, coding: 0, project_coding: 0 },
          selected: res.data.selected || { video: 0, notes: 0, mcq: 0, coding: 0, project_coding: 0 },
          assigned: res.data.assigned || { video: 0, notes: 0, mcq: 0, coding: 0, project_coding: 0 },
          uploaded: res.data.uploaded || { video: 0, notes: 0, mcq: 0, coding: 0, project_coding: 0 },
          approved: res.data.approved || { video: 0, notes: 0, mcq: 0, coding: 0, project_coding: 0 },
        });
      } else {
        setStats(null);
      }
    } catch (error) {
      setStats(null);
    }
  };

  // ---------------- HANDLERS ----------------
  /**
   * Handles track selection change event
   * @param e - The select change event
   */
  const handleTrackChange = (e: SelectChangeEvent<string>) => {
    onTrackChange(e.target.value);
  };

  /**
   * Handles project selection change event
   * Step 1: Extract project ID from event
   * Step 2: Find the selected project in projects array
   * Step 3: Call onProjectChange with project details
   * @param e - The select change event
   */
  const handleProjectChange = (e: SelectChangeEvent<string>) => {
    const projectId = e.target.value;
    const project = projects.find((p) => p.project_id === projectId);
    onProjectChange(
      projectId,
      project?.project_name || "",
      project?.project_allowed_subjects,
      project?.project_data
    );
  };

  // ---------------- RENDER ----------------
  return (
    <div
      style={{
        position: "sticky",
        top: 0,
        zIndex: 1000,
        backgroundColor: "#fff",
        borderBottom: "1px solid #dee2e6",
        padding: "12px 16px",
        boxShadow: "0 2px 4px rgba(0,0,0,0.08)",
      }}
    >
      <div className="d-flex justify-content-between align-items-start gap-3">
        {/* LEFT CONTROLS */}
        <div className="d-flex align-items-center gap-3">
          <MUIFormControl size="small" style={{ minWidth: 180 }}>
            <InputLabel>Track</InputLabel>
            <MUISelect
              value={selectedTrack}
              label="Track"
              onChange={handleTrackChange}
              disabled={loadingTracks || loading}
            >
              <MenuItem value="">Select Track</MenuItem>
              {tracks.map((t) => (
                <MenuItem key={t.track_id} value={t.track_id}>
                  {t.track_name}
                </MenuItem>
              ))}
            </MUISelect>
          </MUIFormControl>

          <MUIFormControl size="small" style={{ minWidth: 180 }}>
            <InputLabel>Project</InputLabel>
            <MUISelect
              value={selectedProject}
              label="Project"
              onChange={handleProjectChange}
              disabled={loadingProjects || loading || !selectedTrack}
            >
              <MenuItem value="">Select Project</MenuItem>
              {projects.map((p) => (
                <MenuItem key={p.project_id} value={p.project_id}>
                  {p.project_name}
                </MenuItem>
              ))}
            </MUISelect>
          </MUIFormControl>
        </div>

        {/* STATS SECTION - Only show when project is selected via filter (not from navigation state) */}
        {selectedProject && isFilterApplied && stats && (
          <div style={{ minWidth: 600 }}>
            {/* HEADERS */}
            <div className="fw-semibold text-center" style={{ ...gridStyle}}>
              <span></span>
              <span>Total</span>
              <span>Selected</span>
              <span>Assigned</span>
              <span>Uploaded</span>
              <span>Approved 
                <span role="button" className="ms-2" onClick={() => setOpenStats(!openStats)}>
                {openStats ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
              </span></span>
              
            </div>

            {/* SUMMARY */}
            <div
              style={{
                ...gridStyle,
                cursor: "pointer"
              }}
              className="text-center"
              onClick={() => setOpenStats(!openStats)}
            >
              <span className="fw-semibold text-start">All</span>
              <span>{getSum(stats.total)}</span>
              <span>{getSum(stats.selected)}</span>
              <span>{getSum(stats.assigned)}</span>
              <span>{getSum(stats.uploaded)}</span>
              <span>{getSum(stats.approved)}</span>
            </div>

            {/* DETAILS */}
            {openStats && (
              <div>
                {[
                  ["Videos", "video"],
                  ["Notes", "notes"],
                  ["MCQs", "mcq"],
                  ["Coding", "coding"],
                  ["Project Coding", "project_coding"],
                ].map(([label, key]) => (
                  <div
                    key={key}
                    style={{ ...gridStyle }}
                    className="text-center"
                  >
                    <span className="fw-semibold text-start">{label}</span>
                    <span>{stats.total[key as keyof typeof stats.total]}</span>
                    <span>{stats.selected[key as keyof typeof stats.selected]}</span>
                    <span>{stats.assigned[key as keyof typeof stats.assigned]}</span>
                    <span>{stats.uploaded[key as keyof typeof stats.uploaded]}</span>
                    <span>{stats.approved[key as keyof typeof stats.approved]}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

       
      </div>
    </div>
  );
};

export default ProjectConfigurationHeader;


